import React from 'react';
import { Button, Input, List, Select } from 'antd';
import type { Dispatch, SetStateAction } from 'react';
import type {
  DockingRecord,
  ProgressFilter,
  ProgressStatistics,
} from '../../types/docking';
import {
  formatOverdueDisplay,
  isRecordOverdue,
} from '../../utils/progressHelpers';

export interface ProgressSidebarProps {
  statistics: ProgressStatistics;
  records: DockingRecord[];
  filter: ProgressFilter;
  setFilter: Dispatch<SetStateAction<ProgressFilter>>;
  selectedRecordId: string | null;
  onSelectRecord: (recordId: string) => void;
  onReselectSameRecord?: (recordId: string) => void;
}

function StatusTag(props: { record: DockingRecord }): React.ReactElement {
  const { record } = props;
  if (record.completionStatus === 'completed') {
    return <span className="progress-tag progress-tag--done">已完成</span>;
  }
  if (record.completionStatus === 'incomplete') {
    return <span className="progress-tag progress-tag--todo">未完成</span>;
  }
  return <span className="progress-tag progress-tag--muted">未填写</span>;
}

export function ProgressSidebar(props: ProgressSidebarProps): React.ReactElement {
  const {
    statistics,
    records,
    filter,
    setFilter,
    selectedRecordId,
    onSelectRecord,
    onReselectSameRecord,
  } = props;

  return (
    <aside className="dash-panel dash-panel--left">
      <div className="dash-section">
        <div className="dash-stat-grid dash-stat-grid--progress">
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">进展条目</div>
            <div className="dash-stat-card__value">{statistics.total}</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">已完成</div>
            <div className="dash-stat-card__value dash-stat-card__value--done">
              {statistics.completed}
            </div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">未完成</div>
            <div className="dash-stat-card__value dash-stat-card__value--todo">
              {statistics.incomplete}
            </div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">已逾期</div>
            <div className="dash-stat-card__value dash-stat-card__value--overdue">
              {statistics.overdue}
            </div>
          </div>
        </div>
      </div>

      <div className="dash-section">
        <div className="dash-field-label">搜索</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            allowClear
            value={filter.keyword ?? ''}
            placeholder="项目 / 后续工作安排"
            onChange={(e) =>
              setFilter((prev) => ({
                ...prev,
                keyword: e.target.value.trim() ? e.target.value : undefined,
              }))
            }
          />
          <Button
            type="default"
            disabled={!filter.keyword?.trim()}
            onClick={() => setFilter((prev) => ({ ...prev, keyword: undefined }))}
          >
            清空
          </Button>
        </div>

        <div className="dash-field-label dash-field-label--spaced">项目类别</div>
        <Select
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.category}
          onChange={(v) => setFilter((prev) => ({ ...prev, category: v }))}
          options={[
            { label: '全部', value: 'all' },
            { label: '前期项目', value: 'preliminary' },
            { label: '履约项目', value: 'performance' },
          ]}
        />

        <div className="dash-field-label dash-field-label--spaced">是否完成</div>
        <Select
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.completion}
          onChange={(v) => setFilter((prev) => ({ ...prev, completion: v }))}
          options={[
            { label: '全部', value: 'all' },
            { label: '已完成', value: 'completed' },
            { label: '未完成', value: 'incomplete' },
            { label: '未填写', value: 'unspecified' },
          ]}
        />

        <div className="dash-field-label dash-field-label--spaced">逾期状态</div>
        <Select
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={filter.overdue}
          onChange={(v) => setFilter((prev) => ({ ...prev, overdue: v }))}
          options={[
            { label: '全部', value: 'all' },
            { label: '已逾期', value: 'overdue' },
            { label: '未逾期', value: 'not_overdue' },
            { label: '未填写', value: 'unspecified' },
          ]}
        />
      </div>

      <div className="dash-section dash-section--grow">
        <div className="dash-field-label">
          项目工作进展（{records.length} 条）
        </div>
        <div className="dash-list-wrap">
          <List
            size="small"
            dataSource={records}
            locale={{ emptyText: '暂无进展记录' }}
            renderItem={(item) => {
              const isSelected = selectedRecordId === item.id;
              const overdue = isRecordOverdue(item);
              return (
                <List.Item
                  className={
                    isSelected
                      ? 'dash-list-item dash-list-item--active'
                      : 'dash-list-item'
                  }
                  onClick={() => {
                    if (selectedRecordId === item.id) {
                      onReselectSameRecord?.(item.id);
                    }
                    onSelectRecord(item.id);
                  }}
                >
                  <div className="dash-list-item__stack">
                    <div className="dash-list-item__title-row">
                      <span className="dash-list-item__name">
                        {item.projectName}
                      </span>
                      <StatusTag record={item} />
                    </div>
                    <span className="dash-list-item__meta">
                      对接 {item.dockedAt || '—'}
                      {item.plannedCompletedAt
                        ? ` · 计划 ${item.plannedCompletedAt}`
                        : ''}
                      {overdue
                        ? ` · 逾期 ${formatOverdueDisplay(item)}`
                        : item.overdueLabel
                          ? ` · ${formatOverdueDisplay(item)}`
                          : ''}
                    </span>
                    {item.followUpPlan ? (
                      <span className="dash-list-item__snippet">
                        {item.followUpPlan.replace(/\s+/g, ' ').slice(0, 72)}
                        {item.followUpPlan.length > 72 ? '…' : ''}
                      </span>
                    ) : null}
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </aside>
  );
}
